import type {
  CreateCustomizationRequest,
  Customization,
  CustomizationOptionInput,
  UpdateCustomizationRequest,
} from '@nucleus-commerce/api-client/admin';

import { BASE_CURRENCY } from '@/shared/catalog/base-currency';

import {
  IMAGE_CONTENT_TYPES,
  type CustomizationFormInput,
  type CustomizationFormValues,
  type CustomizationOptionRow,
} from '../schemas/customization-form-schema';

export function emptyCustomizationForm(): CustomizationFormInput {
  return {
    key: '',
    label: '',
    inputType: 'text',
    isRequired: false,
    priceDelta: { amount: 0, currency: BASE_CURRENCY },
    affectsLeadTimeDays: '0',
    helpText: '',
    optionsSource: 'inline',
    sizeSetId: '',
    options: [],
    maxLength: '',
    pattern: '',
    acceptAny: false,
    accept: [...IMAGE_CONTENT_TYPES],
  };
}

export function fromCustomization(row: Customization): CustomizationFormInput {
  return {
    key: row.key,
    label: row.label,
    inputType: row.inputType,
    isRequired: row.isRequired,
    priceDelta: { amount: row.priceDeltaCents, currency: BASE_CURRENCY },
    affectsLeadTimeDays: String(row.affectsLeadTimeDays),
    helpText: row.helpText ?? '',
    optionsSource: row.sizeSetId ? 'size_set' : 'inline',
    sizeSetId: row.sizeSetId ?? '',
    options: (row.options ?? []).map((o) => ({
      value: o.value,
      label: o.label,
      priceDelta: o.priceDeltaCents ? { amount: o.priceDeltaCents, currency: BASE_CURRENCY } : null,
      existing: true,
    })),
    maxLength: row.maxLength === null ? '' : String(row.maxLength),
    pattern: row.pattern ?? '',
    acceptAny: row.inputType === 'file' && row.accept == null,
    accept: row.accept ?? [...IMAGE_CONTENT_TYPES],
  };
}

/** Lowercase, digits, dots, underscores and hyphens: the backend's option value shape. */
export function optionValue(label: string): string {
  const value = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9_.-]+/g, '-')
    .replace(/^[-_.]+|[-_.]+$/g, '');
  return value || 'option';
}

/** A new row from a typed label: its stored value is derived once and numbered past any clash. */
export function newOptionRow(label: string, taken: readonly string[]): CustomizationOptionRow {
  const base = optionValue(label);
  let value = base;
  let n = 2;
  while (taken.includes(value)) value = `${base}-${n++}`;
  return { value, label: label.trim(), priceDelta: null, existing: false };
}

/**
 * Rows carry their own stored values, so renaming a label never changes what carts reference. An
 * option's extra price travels only when above zero; the list is replaced whole, so leaving it
 * out clears it.
 */
export function toOptions(rows: readonly CustomizationOptionRow[]): CustomizationOptionInput[] {
  return rows.map((row) => ({
    value: row.value.trim(),
    label: row.label.trim(),
    ...(row.priceDelta && row.priceDelta.amount > 0
      ? { priceDeltaCents: row.priceDelta.amount }
      : {}),
  }));
}

function typed(
  values: CustomizationFormValues,
): Pick<UpdateCustomizationRequest, 'options' | 'sizeSetId' | 'maxLength' | 'pattern' | 'accept'> {
  switch (values.inputType) {
    case 'select':
      return values.optionsSource === 'size_set'
        ? { sizeSetId: values.sizeSetId, options: null }
        : { sizeSetId: null, options: toOptions(values.options) };
    case 'text':
      return {
        maxLength: values.maxLength === '' ? null : Number(values.maxLength),
        pattern: values.pattern || null,
      };
    case 'file':
      return { accept: values.acceptAny ? null : values.accept };
    case 'number':
    case 'boolean':
      return {};
  }
}

export function toCustomizationCreate(values: CustomizationFormValues): CreateCustomizationRequest {
  return {
    key: values.key,
    label: values.label,
    inputType: values.inputType,
    isRequired: values.isRequired,
    priceDeltaCents: values.priceDelta?.amount ?? 0,
    affectsLeadTimeDays: Number(values.affectsLeadTimeDays),
    helpText: values.helpText || null,
    ...typed(values),
  };
}

/** Key and input type never change; everything else the type takes is sent. */
export function toCustomizationUpdate(values: CustomizationFormValues): UpdateCustomizationRequest {
  return {
    label: values.label,
    isRequired: values.isRequired,
    priceDeltaCents: values.priceDelta?.amount ?? 0,
    affectsLeadTimeDays: Number(values.affectsLeadTimeDays),
    helpText: values.helpText || null,
    ...typed(values),
  };
}
